import { PUSH_FINGERPRINT_LENGTH } from '@fos/shared/contracts/notifications';

/**
 * Web Push w przeglądarce (Ustawienia → Powiadomienia): zgoda, subskrypcja `pushManager` z kluczem publicznym VAPID
 * z API, odcisk adresu (SHA-256, jak `endpointFingerprint` w @fos/core) do rozpoznania „to urządzenie”.
 * Service worker rejestruje `main.tsx` tylko w buildzie produkcyjnym (`vite build` / `preview` / API z SPA);
 * w `npm run dev` go nie ma — stan „no_service_worker”.
 */

export type PushBrowserSupport = 'supported' | 'unsupported' | 'ios_needs_install';

export interface BrowserSubscriptionJson {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

function isIos(ua: string): boolean {
  return /iphone|ipad|ipod/i.test(ua);
}

function isStandalone(): boolean {
  try {
    return (
      window.matchMedia?.('(display-mode: standalone)').matches === true ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true
    );
  } catch {
    return false;
  }
}

/** Czy ta przeglądarka obsłuży Web Push (iPhone: tylko aplikacja dodana do ekranu początkowego, iOS 16.4+). */
export function pushSupport(): PushBrowserSupport {
  const ok =
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;
  if (ok) return 'supported';
  if (typeof navigator !== 'undefined' && isIos(navigator.userAgent) && !isStandalone()) return 'ios_needs_install';
  return 'unsupported';
}

export function notificationPermission(): NotificationPermission | null {
  return typeof Notification === 'undefined' ? null : Notification.permission;
}

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (typeof Notification === 'undefined') return 'denied';
  return Notification.requestPermission();
}

/** Klucz VAPID (base64url) → `applicationServerKey`. */
export function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function sameKey(a: ArrayBuffer | null | undefined, b: Uint8Array): boolean {
  if (!a) return false;
  const x = new Uint8Array(a);
  if (x.length !== b.length) return false;
  for (let i = 0; i < x.length; i++) if (x[i] !== b[i]) return false;
  return true;
}

/** Odcisk adresu subskrypcji: hex SHA-256 (pierwsze `PUSH_FINGERPRINT_LENGTH` znaków), jak na serwerze. */
export async function endpointFingerprint(endpoint: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(endpoint));
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, PUSH_FINGERPRINT_LENGTH);
}

/** Rejestracja service workera aplikacji (null: tryb deweloperski albo przeglądarka bez SW). */
export async function serviceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  return (await navigator.serviceWorker.getRegistration()) ?? null;
}

function toJson(sub: PushSubscription): BrowserSubscriptionJson {
  const json = sub.toJSON();
  const keys = json.keys ?? {};
  if (!json.endpoint || !keys.p256dh || !keys.auth) throw new Error('Przeglądarka zwróciła niepełną subskrypcję');
  return { endpoint: json.endpoint, keys: { p256dh: keys.p256dh, auth: keys.auth } };
}

/** Bieżąca subskrypcja tej przeglądarki (null: brak SW albo brak subskrypcji). */
export async function currentBrowserSubscription(): Promise<BrowserSubscriptionJson | null> {
  const reg = await serviceWorkerRegistration();
  const sub = await reg?.pushManager.getSubscription();
  return sub ? toJson(sub) : null;
}

/**
 * Subskrypcja z kluczem VAPID instalacji. Istniejąca z tym samym kluczem jest używana ponownie; z innym kluczem
 * (zmiana kluczy na serwerze) — wyrejestrowana i założona od nowa.
 */
export async function subscribeBrowser(publicKey: string): Promise<BrowserSubscriptionJson> {
  const reg = await serviceWorkerRegistration();
  if (!reg) throw new Error('Brak service workera aplikacji');
  const key = urlBase64ToUint8Array(publicKey);
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    if (sameKey(existing.options.applicationServerKey, key)) return toJson(existing);
    await existing.unsubscribe();
  }
  const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  return toJson(sub);
}

/** Wyrejestrowanie subskrypcji tej przeglądarki (bez błędu, gdy jej nie ma). */
export async function unsubscribeBrowser(): Promise<void> {
  const reg = await serviceWorkerRegistration();
  const sub = await reg?.pushManager.getSubscription();
  await sub?.unsubscribe();
}

/** Nazwa urządzenia z User-Agent („Chrome na Androidzie”, „Safari na iPhonie”, „Edge na Windows”). */
export function deviceLabelFromUserAgent(ua: string): string {
  const browser = /edg\//i.test(ua)
    ? 'Edge'
    : /opr\//i.test(ua)
      ? 'Opera'
      : /firefox|fxios/i.test(ua)
        ? 'Firefox'
        : /chrome|crios|chromium/i.test(ua)
          ? 'Chrome'
          : /safari/i.test(ua)
            ? 'Safari'
            : 'Przeglądarka';
  const system = /android/i.test(ua)
    ? 'Androidzie'
    : /iphone/i.test(ua)
      ? 'iPhonie'
      : /ipad/i.test(ua)
        ? 'iPadzie'
        : /windows/i.test(ua)
          ? 'Windows'
          : /mac os x|macintosh/i.test(ua)
            ? 'macOS'
            : /linux/i.test(ua)
              ? 'Linuksie'
              : null;
  return system ? `${browser} na ${system}` : browser;
}

/** Telefon albo tablet (ikona w liście urządzeń). */
export function isMobileLabel(label: string): boolean {
  return /android|iphon|ipad|telefon|mobile/i.test(label);
}
